import { useState, useRef } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import AmmoLogo from "../components/AmmoLogo";
import CoreLoginPanel from "../components/CoreLoginPanel";

// Built from a JS string rather than written into the JSX attribute: escape
// sequences are interpreted in JavaScript strings but taken literally inside
// JSX attribute quotes, which is how this placeholder once ended up showing
// its own escape codes.
const PASSWORD_PLACEHOLDER = "\u2022".repeat(8);

const CODE_LENGTH = 6;
const EMPTY_CODE = Array(CODE_LENGTH).fill("");

export default function Login() {
  const { user, loading: authLoading, login, verify2FA, resend2FA } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [accountLocked, setAccountLocked] = useState(false);

  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [twoFAEmail, setTwoFAEmail] = useState("");
  const [code, setCode] = useState(EMPTY_CODE);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [twoFAError, setTwoFAError] = useState("");
  const [resendMsg, setResendMsg] = useState("");
  const codeRefs = useRef([]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-6 h-6 border-2 border-indigo-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setAccountLocked(false);
    setLoading(true);

    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      navigate("/");
    } else if (result.requires_2fa) {
      setTwoFAEmail(result.email);
      setShow2FA(true);
      setCode(EMPTY_CODE);
      setTwoFAError("");
    } else if (result.locked) {
      setAccountLocked(true);
    } else {
      setError(result.error);
    }
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setTwoFAError("");

    // Auto-focus next input
    if (value && index < CODE_LENGTH - 1) {
      codeRefs.current[index + 1]?.focus();
    }

    // Submit as soon as the last empty box is filled, wherever that is. This
    // used to fire only when the box being typed in was the last one, so
    // correcting a digit in the middle left a complete code sitting there
    // waiting for a button nobody had a reason to look for.
    if (value && newCode.every((d) => d !== "")) {
      handleVerify(newCode.join(""));
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    // Non-digits are dropped, so a code pasted out of an email with a trailing
    // space or a line break still lands. A short paste fills what it can and
    // leaves the caret after it, rather than being ignored outright, which is
    // what happened to anything that was not exactly six characters.
    const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH);
    if (!digits) return;

    const newCode = [...EMPTY_CODE];
    digits.split("").forEach((d, i) => { newCode[i] = d; });
    setCode(newCode);
    setTwoFAError("");

    const next = Math.min(digits.length, CODE_LENGTH - 1);
    codeRefs.current[next]?.focus();

    if (digits.length === CODE_LENGTH) {
      handleVerify(digits);
    }
  };

  const handleVerify = async (codeStr) => {
    setVerifying(true);
    setTwoFAError("");
    const result = await verify2FA(twoFAEmail, codeStr);
    setVerifying(false);
    if (result.success) {
      navigate("/");
    } else {
      setTwoFAError(result.error);
      setCode(EMPTY_CODE);
      codeRefs.current[0]?.focus();
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendMsg("");
    setTwoFAError("");
    const result = await resend2FA(twoFAEmail);
    setResending(false);
    if (result.success) {
      setResendMsg(t("login.2fa.codeSent"));
      setCode(EMPTY_CODE);
      codeRefs.current[0]?.focus();
    } else {
      setTwoFAError(result.error);
    }
  };

  const handleBack = () => {
    setShow2FA(false);
    setTwoFAEmail("");
    setCode(EMPTY_CODE);
    setTwoFAError("");
    setResendMsg("");
  };

  const LanguageToggle = () => (
    <div className="flex justify-center gap-2 mt-4">
      <button onClick={() => setLanguage("en")} className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${language === "en" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500"}`}>EN</button>
      <button onClick={() => setLanguage("it")} className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${language === "it" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500"}`}>IT</button>
    </div>
  );

  // ===== 2FA VERIFICATION SCREEN =====
  if (show2FA) {
    return (
      <div className="min-h-screen flex">
        <div className="hidden lg:flex lg:w-1/2">
          <CoreLoginPanel />
        </div>
        <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-white">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
            <button onClick={handleBack} className="inline-flex items-center text-sm text-slate-500 hover:text-indigo-700 mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4 mr-1" />
              {t("login.2fa.back")}
            </button>

            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="text-2xl font-bold tracking-tight text-slate-900">{t("login.2fa.title")}</h2>
            </div>
            <p className="text-slate-500 mb-8 break-words">{t("login.2fa.subtitle", { email: twoFAEmail })}</p>

            {/* Six boxes at 48px with 8px between them need 328px, and a 360px
                phone with the padding around them leaves 296. They were
                overflowing the screen on the one page everybody has to get
                through. Smaller below sm, unchanged above it. */}
            <div className="flex justify-center gap-1.5 sm:gap-2 mb-6" onPaste={handleCodePaste}>
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (codeRefs.current[i] = el)}
                  data-testid={`2fa-code-${i}`}
                  type="text"
                  inputMode="numeric"
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(i, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(i, e)}
                  autoFocus={i === 0}
                  className="w-10 h-12 sm:w-12 sm:h-14 text-center text-lg sm:text-xl font-semibold border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                />
              ))}
            </div>

            {twoFAError && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-4" data-testid="2fa-error">
                {twoFAError}
              </motion.div>
            )}

            {resendMsg && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 text-sm mb-4">
                {resendMsg}
              </motion.div>
            )}

            <Button
              onClick={() => handleVerify(code.join(""))}
              disabled={verifying || code.some((d) => !d)}
              data-testid="2fa-verify-btn"
              className="w-full h-11 bg-indigo-700 hover:bg-indigo-600 text-white font-medium rounded-lg mb-3"
            >
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : t("login.2fa.verify")}
            </Button>

            <div className="text-center">
              <button onClick={handleResend} disabled={resending} data-testid="2fa-resend-btn" className="text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors">
                {resending ? t("login.2fa.sending") : t("login.2fa.resend")}
              </button>
            </div>

            <p className="mt-6 text-center text-xs text-slate-400">{t("login.2fa.expires")}</p>
          </motion.div>
        </div>
      </div>
    );
  }

  // ===== LOGIN SCREEN =====
  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2">
        <CoreLoginPanel />
      </div>
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-white">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
          <div className="lg:hidden mb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-4">
              <AmmoLogo className="w-12 h-12" />
            </div>
            <h1 className="text-xl font-bold text-slate-800">AMMO Core</h1>
          </div>
          <div className="mb-10">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 mb-2">{t("login.title")}</h2>
            <p className="text-slate-500">{t("login.subtitle")}</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">{t("login.email")}</Label>
              <Input id="email" data-testid="login-email-input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" required className="h-11 border-slate-200 focus:ring-1 focus:ring-indigo-600 focus:border-indigo-600" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">{t("login.password")}</Label>
              <Input id="password" data-testid="login-password-input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={PASSWORD_PLACEHOLDER} required className="h-11 border-slate-200 focus:ring-1 focus:ring-indigo-600 focus:border-indigo-600" />
            </div>
            {accountLocked && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="login-locked-error">
                <p className="text-red-700 text-sm font-medium mb-2">{t("login.accountLocked")}</p>
                <Link to="/forgot-password" className="text-sm text-indigo-600 hover:text-indigo-800 font-medium underline">{t("login.forgotPassword")}</Link>
              </motion.div>
            )}
            {error && !accountLocked && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="login-error">
                {error}
              </motion.div>
            )}
            <Button type="submit" data-testid="login-submit-btn" disabled={loading} className="w-full h-11 bg-indigo-700 hover:bg-indigo-600 text-white font-medium rounded-lg btn-scale">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("login.submit")}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors">
              {t("login.forgotPassword")}
            </Link>
          </div>
          <p className="mt-4 text-center text-sm text-slate-400">{t("login.contactAdmin")}</p>
          <LanguageToggle />
        </motion.div>
      </div>
    </div>
  );
}
